import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const AuthContext = createContext();
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000; // 20 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('pulse_token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const silentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('pulse_refresh');
    if (!refreshToken) return null;
    try {
      const res = await fetch(`${API}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('pulse_token', data.token);
        setToken(data.token);
        return data.token;
      }
    } catch { /* silent fail */ }
    return null;
  }, []);

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(silentRefresh, TOKEN_REFRESH_INTERVAL);
  }, [silentRefresh]);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const refreshUser = async (activeToken = token) => {
    if (!activeToken) return null;
    const res = await fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${activeToken}` }
    });
    if (res.status === 401) {
      const newToken = await silentRefresh();
      if (newToken) {
        const retry = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` }
        });
        if (retry.ok) {
          const nextUser = await retry.json();
          setUser(nextUser);
          return nextUser;
        }
      }
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error('Unable to refresh user');
    const nextUser = await res.json();
    setUser(nextUser);
    return nextUser;
  };

  useEffect(() => {
    if (token) {
      refreshUser(token)
        .then(() => { setLoading(false); startRefreshTimer(); })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => stopRefreshTimer();
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('pulse_token', data.token);
    localStorage.setItem('pulse_refresh', data.refresh_token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Register failed');
    }
    const data = await res.json();
    localStorage.setItem('pulse_token', data.token);
    localStorage.setItem('pulse_refresh', data.refresh_token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const loginWithToken = (newToken, userData) => {
    localStorage.setItem('pulse_token', newToken);
    if (userData?.refresh_token) localStorage.setItem('pulse_refresh', userData.refresh_token);
    setToken(newToken);
    setUser(userData);
  };

  const updateUser = (updates) => {
    setUser((current) => ({ ...(current || {}), ...(updates || {}) }));
  };

  const logout = () => {
    stopRefreshTimer();
    localStorage.removeItem('pulse_token');
    localStorage.removeItem('pulse_refresh');
    setToken(null);
    setUser(null);
  };

  // Must match Dashboard: /api/auth/me sets is_settings_owner, but first paint after login
  // can lack it; keep same email fallback as DashboardPage isSettingsOwner so the director
  // still sees "Direccion · departamentos" and the route guard does not break.
  const isOwner = user?.role === 'admin';
  const emailLower = String(user?.email || '').toLowerCase();
  const dirEmail = String(user?.director_email || 'pulsestockia@gmail.com')
    .trim()
    .toLowerCase();
  const isDirector =
    isOwner &&
    Boolean(user?.is_settings_owner ?? (emailLower && emailLower === dirEmail));
  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithToken,
      logout,
      refreshUser,
      updateUser,
      needsOnboarding: user?.onboarding_completed === false,
      isAdmin: user?.role === 'admin' || user?.role === 'pluka',
      isModerator: user?.role === 'admin' || user?.role === 'pluka' || user?.role === 'moderator',
      isDirector,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
